import {useCallback, useEffect, useState} from "react";
import axios from "axios";
import {PATHS_API} from "../../../enum";

interface IProblemPayment {
    _id: string,
    orderId: string,
    steamId: string,
    amount: number,
    paidAmount?: number,
    paymentSystem: string,
    providerPaymentId?: string,
    code?: string,
    reviewReason?: string,
    reviewDetails?: string,
    donationMessage?: string,
    providerPayload?: string,
    createdAt: string,
}

const REASONS: Record<string, string> = {
    no_payment_id: "У вебхуку немає ID платежу",
    bad_amount: "Некоректна сума",
    no_steam_id: "Не вдалося визначити гравця",
    user_not_found: "Гравця немає в базі",
    credit_failed: "Помилка зарахування",
    underpaid: "Недоплата",
    not_paid: "Банк не підтвердив оплату",
    amount_mismatch: "Сума не збігається",
    refunded: "Платіж повернуто банком",
    stuck: "Обробка обірвалась",
};

// Платежі, які не зарахувались автоматично (Донатік): усе, що відомо, щоб адмін міг розібратись і зарахувати вручну
const PaymentProblems = ({ onError }: { onError: (message: string) => void }) => {
    const [list, setList] = useState<IProblemPayment[]>([]);
    const [open, setOpen] = useState<string | null>(null);

    const load = useCallback(() => {
        axios.get(PATHS_API.ADMIN_PAYMENT_PROBLEMS, { withCredentials: true })
            .then(({ data }) => setList(data))
            .catch(() => {});
    }, []);

    // нові проблеми з'являються самі, поки адмінка відкрита
    useEffect(() => {
        load();
        const timer = window.setInterval(load, 60_000);
        return () => window.clearInterval(timer);
    }, [load]);

    const markReviewed = async (p: IProblemPayment) => {
        if (!window.confirm(`Позначити платіж ${p.orderId} розібраним? Він зникне з цього списку.`)) return;
        try {
            await axios.post(`${PATHS_API.ADMIN_PAYMENTS}/${p._id}/reviewed`, {}, { withCredentials: true });
            setList((l) => l.filter((x) => x._id !== p._id));
        } catch (err: any) {
            onError(err.response?.data?.message || "Не вдалося позначити платіж");
        }
    };

    if (!list.length) return null;

    return (
        <div className="adm-problems">
            <div className="adm-problems-head">
                <span className="adm-problems-icon" aria-hidden="true">!</span>
                <div>
                    <b>Не зараховані платежі: {list.length}</b>
                    <span>Гроші прийшли, але автоматично на баланс не потрапили. Розберіться й зарахуйте вручну.</span>
                </div>
            </div>

            { list.map((p) =>
                <div key={p._id} className="adm-problem">
                    <div className="adm-problem-top">
                        <span className="adm-problem-reason">{REASONS[p.reviewReason ?? ""] ?? p.reviewReason ?? "Невідома причина"}</span>
                        <span className="adm-problem-sum">{p.paidAmount ?? p.amount} ₴</span>
                        <span className="adm-muted">{new Date(p.createdAt).toLocaleString("uk-UA")}</span>
                    </div>
                    <p className="adm-problem-details">{p.reviewDetails}</p>
                    <dl className="adm-problem-facts">
                        <dt>SteamID</dt><dd className="adm-mono">{p.steamId}</dd>
                        <dt>Повідомлення доната</dt><dd>{p.donationMessage || "—"}</dd>
                        <dt>ID у Донатіку</dt><dd className="adm-mono">{p.providerPaymentId || "—"}</dd>
                        <dt>Наш платіж</dt><dd className="adm-mono">{p.orderId}{p.code ? ` (код ${p.code})` : ""}</dd>
                        <dt>Сума на сайті / прийшло</dt><dd>{p.amount} ₴ / {p.paidAmount ?? "—"} ₴</dd>
                    </dl>
                    <div className="adm-problem-actions">
                        { p.providerPayload &&
                            <button type="button" className="adm-ghost-btn" onClick={() => setOpen(open === p._id ? null : p._id)}>
                                {open === p._id ? "Сховати вебхук" : "Показати вебхук"}
                            </button>
                        }
                        <button type="button" className="adm-primary-btn" onClick={() => markReviewed(p)}>Розібрано</button>
                    </div>
                    { open === p._id && <pre className="adm-problem-raw">{p.providerPayload}</pre> }
                </div>
            )}
        </div>
    );
};

export default PaymentProblems;
